import { Link } from 'react-router-dom';
import competitionsData from '@/data/competitions.json';
import type { CompetitionEvent } from '@/data/types';

const events = competitionsData as CompetitionEvent[];
// Most recent event (events without a date sort last); used for the results banner.
const latestEvent = [...events].sort((a, b) => (b.date || '').localeCompare(a.date || ''))[0];

export default function Hero() {
  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" className="relative min-h-screen flex flex-col items-center justify-center overflow-x-hidden">
      {/* Background Video */}
      <div className="absolute inset-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover object-center"
          aria-hidden
        >
          <source src="assets/HeroVideo.mp4" type="video/mp4" />
        </video>
        {/* Multi-stop overlay: dark top for navbar legibility, lighter mid, dark bottom to bleed into next section */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/40 to-black/85" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full container mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center pt-28 sm:pt-32">
        {/* Results banner — first thing visitors see; links to the leaderboard */}
        {latestEvent && (
          <Link
            to="/events"
            className="hero-animate hero-animate-1 group inline-flex items-center gap-2.5 mb-6 bg-[#14B8A6]/15 hover:bg-[#14B8A6]/25 border border-[#14B8A6]/40 text-white rounded-full pl-2.5 pr-4 py-2 text-sm font-medium backdrop-blur-sm transition-all duration-300 hover:shadow-[0_0_24px_rgba(20,184,166,0.4)] max-w-[92vw]"
          >
            <span className="inline-flex items-center gap-1.5 bg-[#14B8A6] text-white text-[10px] font-bold tracking-wider uppercase px-2 py-1 rounded-full flex-shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              Live
            </span>
            <span className="truncate">
              {latestEvent.discipline} results are out — find your ranking
            </span>
            <i className="ri-arrow-right-line text-[#14B8A6] group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
          </Link>
        )}

        {/* Decorative accent line above headline */}
        <div className="hero-animate hero-animate-1 flex items-center gap-3 mb-6">
          <span className="w-8 h-px bg-[#14B8A6]" />
          <span className="text-[#14B8A6] text-xs font-semibold tracking-[0.25em] uppercase">Jbeil, Lebanon</span>
          <span className="w-8 h-px bg-[#14B8A6]" />
        </div>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight">
          <span className="block hero-animate hero-animate-2">One Gym.</span>
          <span className="block hero-animate hero-animate-3">Multiple Disciplines.</span>
          <span className="block hero-animate hero-animate-4 text-[#14B8A6]">No Limits.</span>
        </h1>

        <p className="hero-animate hero-animate-5 text-lg sm:text-xl lg:text-2xl text-white/75 mb-10 sm:mb-12 max-w-2xl mx-auto px-4 leading-relaxed">
          Train harder, move better, recover stronger — CrossFit, calisthenics, strength training, and physiotherapy, all under one roof.
        </p>

        {/* CTA Buttons */}
        <div className="hero-animate hero-animate-5 flex flex-col sm:flex-row items-center gap-4 px-4 w-full sm:w-auto">
          <button
            onClick={() => scrollToSection('classes')}
            className="w-full sm:w-auto bg-[#14B8A6] text-white px-10 sm:px-14 py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-[#0F9A8A] transition-all duration-300 shadow-lg hover:shadow-[0_0_28px_rgba(20,184,166,0.5)] whitespace-nowrap cursor-pointer min-h-[52px]"
          >
            View Classes
          </button>
          <button
            onClick={() => scrollToSection('membership')}
            className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border border-white/20 text-white px-10 sm:px-14 py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-white/20 hover:border-white/40 transition-all duration-300 whitespace-nowrap cursor-pointer min-h-[52px]"
          >
            See Pricing
          </button>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="relative z-10 mt-16 mb-6 flex flex-col items-center gap-2 animate-bounce">
        <div className="w-7 h-11 border-2 border-white/60 rounded-full flex items-start justify-center p-1.5">
          <div className="w-1.5 h-2.5 bg-white/70 rounded-full" />
        </div>
      </div>

      {/* Bottom fade — blends into the next section's bg (#000) */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent pointer-events-none" />
    </section>
  );
}
